import Backbone from 'backbone'
import _ from 'lodash'

import ColorPicker from 'scoreboard/components/color-picker'
import FormView from 'scoreboard/components/form-view'
import i18n from 'scoreboard/components/i18n'
import LabeledToggle from 'scoreboard/components/labeled-toggle'
import ThresholdColorsModel from 'scoreboard/components/threshold-colors/threshold-colors-model'
import ThresholdColors from 'scoreboard/components/threshold-colors/threshold-colors'
import unwrapModel from 'scoreboard/components/unwrap-model'

import template from './shape-color-config-view.hbs'
import { SPECIFIC_COLOR, CONDITIONAL, PERFORMANCE } from '../shape-color-type'
import { SHAPE_COLOR_TYPE, SHAPE_SPECIFIC_COLOR, DEFAULT_SHAPE_COLOR } from '../shape-widget-properties'
import { NONE } from '../shape-fill-type'
import * as commandOptions from 'scoreboard/components/rich-text/command-options'

/**
 * Dialog view for the Set Text option in the shape widget's config menu
 */
const ShapeColorConfigView = Backbone.View.extend({
	className: 'shape-text-config-view',
	tagName: 'form',

	render() {
		this.$el.html(template({
			dialogTitle: getMenuLabel(this.model)
		}))

		this.formView = new FormView({
			el: this.el,
			submit: () => this.submit()
		})

		this.renderShapeColorTypeToggle()
		this.renderSpecificColorInput()
		this.renderConditionalColorInput()

		this.updateShapeSpecificColorVisibility()
		this.updateConditionalColorVisibility()

		this.formView.start()

		return this
	},

	/**
	 * Renders the shapeColorType toggle (ie "Specific Color | Score Color | Conditional")
	 */
	renderShapeColorTypeToggle () {
		this.shapeColorTypeToggle = new LabeledToggle({
			value: this.model.properties.get(SHAPE_COLOR_TYPE),
			options: this.getShapeColorTypeOptions()
		})

		this.$('.shape-color-type-toggle').append(this.shapeColorTypeToggle.el)
		this.shapeColorTypeToggle.render()

		if (this.model.isNoneSource()) {
			// SPECIFIC_COLOR is the only option for "None" source, so we just
			// hide the toggle
			this.$('.shape-color-type-section').addClass('is-not-displayed')
		}

		this.shapeColorTypeToggle.on('change', () => this.onShapeColorTypeChange())
	},

	/**
	 * Returns options for the shapeTextType toggle
	 */
	getShapeColorTypeOptions () {
		let options = []

		options.push({
			value: SPECIFIC_COLOR,
			label: 'dashboards.specificColor'
		})

		if (!this.model.isDatasetSource()) {
			options.push({
				value: PERFORMANCE,
				label: 'dashboards.scoreColor'
			})
		}

		options.push({
			value: CONDITIONAL,
			label: 'report.designer.cellColor.conditional'
		})

		return options
	},

	onShapeColorTypeChange () {
		this.formView.change()
		this.updateShapeSpecificColorVisibility()
		this.updateConditionalColorVisibility()
	},

	/**
	 * Renders the color picker used for "Specific Color"
	 */
	renderSpecificColorInput () {
		this.colorPicker = new ColorPicker({
			// Using these colors (same as the the rich text editor) because it
			// includes all the standard scoring colors, plus black and white
			colors: commandOptions.rawColors()
		})
		this.$('.shape-specific-color-section').append(this.colorPicker.el)
		this.colorPicker.render()
		this.colorPicker.color(
			this.model.properties.get(SHAPE_SPECIFIC_COLOR) ?? DEFAULT_SHAPE_COLOR)

		this.colorPicker.on('select', () => {
			this.formView.change()
		})
	},

	updateShapeSpecificColorVisibility () {
		this.$('.shape-specific-color-section').toggleClass('is-not-displayed',
			this.shapeColorTypeToggle.val() !== SPECIFIC_COLOR)
	},

	renderConditionalColorInput () {
		let adjustedColoring = _.cloneDeep(this.model.wob.get('thresholdColoring'))
		if (adjustedColoring?.thresholds) {
			// Shape thresholds have null at the start/end, but they aren't
			// rendered in the view, so trim them off
			adjustedColoring.thresholds = adjustedColoring.thresholds.slice(1, -1)
		}
		let colorModel = new ThresholdColorsModel(adjustedColoring)

		this.thresholdColorsView = new ThresholdColors({
			model: colorModel,
			formView: this.formView,
			isShapeWidget: true
		})
		this.$('.conditional-colors').append(this.thresholdColorsView.el)
		this.thresholdColorsView.render()
	},

	updateConditionalColorVisibility () {
		this.$('.conditional-colors').toggleClass('is-not-displayed',
			this.shapeColorTypeToggle.val() !== CONDITIONAL)
	},

	/**
	 * Returns the current values of the form inputs
	 */
	val () {
		let shapeColorType = this.shapeColorTypeToggle.val()
		let shapeSpecificColor = shapeColorType === SPECIFIC_COLOR
			? this.colorPicker.val() : null

		let adjustedColoring = unwrapModel(this.thresholdColorsView.model)
		if (!_.isEmpty(adjustedColoring.thresholds)) {
			// Add back in the null start/end thresholds that were removed in
			// renderConditionalColorInput()
			adjustedColoring.thresholds = [null, ...adjustedColoring.thresholds, null]
		}

		return {
			shapeColorType,
			shapeSpecificColor,
			thresholdColoring: shapeColorType === CONDITIONAL
				? adjustedColoring : null
		}
	},

	submit () {
		this.trigger('done', this.val())
	},

	autoRemove: true
})

function getMenuLabel (shapeModel) {
	return i18n.get(shapeModel.properties.get('fill') === NONE
		? 'dashboards.setTextColor'
		: 'dashboards.setFillColor')
}

export default ShapeColorConfigView
export { getMenuLabel }

ShapeColorConfigView.__filename__ = 'dashboards/widgets/shape/config/shape-color-config-view.js'
